import { Component, computed, Input } from '@angular/core';
import { IEventTemplate, IDepartmentDTO, IUser, WithFiles, ITag, TagType } from '@vierkant-software/types__api';
import { Duration } from 'luxon';
import { RRule } from 'rrule';
import { Globals } from 'src/app/services/globals.service';
import { TagService } from 'src/app/services/tag.service';
import { AppService } from 'src/services/app.service';
import { toDict } from 'src/util/array';
import { Dict } from 'src/util/types/global';

/**
* @ignore - do not show in documentation
*/
@Component({
    selector:    'v-event-template-summary',
    templateUrl: './v-event-template-summary.component.haml',
    styleUrls:   ['./v-event-template-summary.component.sass'],
    standalone:  false
})
export class VEventTemplateSummaryComponent {
    public recurrence: RRule;
    private _value: IEventTemplate;
    public get value(): IEventTemplate {
        return this._value;
    }

    @Input() public set value(value: IEventTemplate) {
        const oldID = this._value?.ID;
        this._value = value;
        if (value.users_ID?.length){
            if (value.ID === oldID) return;
            this.fetchData().catch(e => e?.displayErrorToast());
        }
    }

    @Input() showTitle = true;

    @Input() users: WithFiles<IUser[]> = {data: [], __files: []};
    @Input() excludedUsers: WithFiles<IUser[]> = {data: [], __files: []};
    @Input() public departments: Dict<IDepartmentDTO> = {};

    tags = computed<Dict<ITag>>(() => {
        const tags = this.tagService.tags();
        return toDict(tags[TagType.Event] ?? [], x => x.ID);
    });

    constructor(
        private app: AppService,
        private tagService: TagService,
        public globals: Globals
    ) {}

    public durationString(duration: Duration) {
        return duration.reconfigure({locale: this.app.locale}).toHuman();
    }

    public getDurationPerPerson() {
        return this.value.settings.durationPerPerson?.as('minutes');
    }

    public getDurationAsHours(duration: Duration) {
        return duration?.as('hours');
    }

    public getRecurrenceDay() {
        const dayNames = this.app.getTranslation("dayNames") as string[];
        return this.recurrence?.options.byweekday?.sort((a,b) => a - b)
            .map(x => dayNames[(x + 1) % 7]).join(', ');
    }

    public getRecurrenceMinute() {
        return this.recurrence?.options.byminute?.map(x => 'xx:' + String(x).padStart(2, '0')).join(', ');
    }

    public isWholeDay(duration: Duration) {
        return duration?.as("days") === 1;
    }

    public get administration(){
        return this.value.restrictions[0];
    }

    public get selfService(){
        return this.value.restrictions[1];
    }

    // /**
    //  * Departments and Users should be provided through the input properties and preloaded, 
    //  * but if they cannot be provided they will be loaded when the event is assigned
    //  */
    public async fetchData() {
        await this.fetchUsers();
        await this.fetchDepartments();
    }
    private async fetchDepartments() {
        const {departments_ID} = this.value;
        if (!departments_ID?.length) return;
        const existingIDs = Object.keys(this.departments);
        const toFetch = departments_ID.filter(id => !existingIDs.includes(id));
        if (!toFetch.length) return;

        const departments = (await this.app.api.OrgchartWorker.getDepartmentList()).filter(x => toFetch.includes(x.departmentID));
        this.departments = {...this.departments, ...toDict(departments, x => x.departmentID)};
    }
    private async fetchUsers() {
        const userIDs = [...this.value.users_ID ?? [], ...this.value.excludedUsers_ID ?? []].filter(x => !!x);
        if (userIDs.length === 0) return;

        const existingIDs = Object.keys(this.users.data).concat(Object.keys(this.excludedUsers.data));
        const toFetch = userIDs.filter(id => !existingIDs.includes(id));
        if (!toFetch.length) return;

        const users = (await this.app.api.UserWorker.getUsers(toFetch));

        this.users = users.data.reduce((acc, user, index) => {
            if (!this.value.users_ID?.includes(user.ID)) return acc;
            acc.data.push(user);
            acc.__files.push(users.__files[index]);
            return acc;
        }, {data: [], __files: []});

        this.excludedUsers = users.data.reduce((acc, user, index) => {
            if (!this.value.excludedUsers_ID?.includes(user.ID)) return acc;
            acc.data.push(user);
            acc.__files.push(users.__files[index]);
            return acc;
        }, {data: [], __files: []});
    }
}
